import Keycloak, { type KeycloakConfig } from 'keycloak-js'
import ms from 'ms'

import { logError } from '../debug'
import { DefaultError } from '../errors'
import type { StoreReadOnly } from '../store/create'
import { subscribableCreate } from '../subscribable'
import { getKeycloakUser, type KeycloakUser } from './getKeycloakUser'
import type { KeycloakToken } from './KeycloakToken'

const updateSkew = ms('10s')
const updateRefreshSkew = ms('10m')

export type KeycloakOptions<TContext = unknown> = {
	getContext?: (token: string, user: KeycloakUser, context?: TContext) => Awaitable<TContext>
	initialContext?: TContext
}

export type KeycloakState<TContext = unknown> =
	| { status: 'uninitialized'; user?: undefined; context?: TContext; expiration?: undefined }
	| { status: 'initializing'; user?: undefined; context?: TContext; expiration?: undefined }
	| {
			status: 'unauthenticated'
			user?: undefined
			context?: undefined
			expiration?: undefined
			reason: 'unauthenticated' | 'expired' | 'context' | 'error'
	  }
	| {
			status: 'authenticated'
			user: KeycloakUser
			context: TContext
			expiration: Date
	  }

// Hack to have window/document available in utils, which is generally both browser and
// node. At some point, this and other relevant utils should probably be moved
// to something akin to `@subaio/web`, to mirror `@subaio/server`. Maybe
// `@subaio/utils` should be renamed to `@subaio/common`.
declare const window: any
declare const document: any

const getRemainingTime = (token: KeycloakToken) => token.exp * 1000 - Date.now()

export type KeycloakWrapper<TContext = unknown> = StoreReadOnly<KeycloakState<TContext>> & {
	getToken: () => Promise<string | null>
	/** Ensure authentication is initialized */
	initialize: () => void
	hasRole: (role: string) => boolean
	login: () => void
	logout: () => void
	setContext: (context: Partial<TContext>) => void
}

export const keycloakCreate = <TContext = unknown>(
	config: KeycloakConfig,
	{ getContext, initialContext }: KeycloakOptions<TContext> = {},
): KeycloakWrapper<TContext> => {
	const instance = new Keycloak(config)
	const { subscribe, update } = subscribableCreate()

	// Logic to keep login active as long as there is activity on the page
	let isRefreshing = false
	const refreshExpiration = async () => {
		if (isRefreshing || !instance.refreshTokenParsed) return
		// Only attempt to refresh if the token expires soon, but havent yet
		const remainingTime = getRemainingTime(instance.refreshTokenParsed as KeycloakToken)
		if (remainingTime > updateRefreshSkew || remainingTime <= 0) return

		isRefreshing = true
		await instance.updateToken(updateRefreshSkew)
		isRefreshing = false
	}
	document.body.addEventListener('mousemove', refreshExpiration)
	document.body.addEventListener('keydown', refreshExpiration)
	if (process.env.NODE_ENV === 'development') {
		// In development only, keep token alive always
		setInterval(refreshExpiration, ms('2m'))
	}

	let state: KeycloakState<TContext> = {
		status: 'uninitialized',
		context: initialContext,
	}

	const setState = (newState: KeycloakState<TContext>) => {
		state = newState
		update()
	}

	let logoutTimeout: Timeout | undefined
	const handleUpdate = async () => {
		if (logoutTimeout !== undefined) {
			clearTimeout(logoutTimeout)
			logoutTimeout = undefined
		}

		const remainingTime = instance.refreshTokenParsed
			? getRemainingTime(instance.refreshTokenParsed as KeycloakToken)
			: 0
		if (instance.refreshTokenParsed && remainingTime > 0) {
			logoutTimeout = setTimeout(handleUpdate, remainingTime)
		}

		if (!instance.authenticated) {
			return setState({ status: 'unauthenticated', reason: 'unauthenticated' })
		}

		if (remainingTime <= 0) {
			return setState({ status: 'unauthenticated', reason: 'expired' })
		}

		try {
			const user = getKeycloakUser(instance.tokenParsed as KeycloakToken)
			const context = (await getContext?.(instance.token!, user, state.context)) as TContext
			setState({
				status: 'authenticated',
				user,
				context,
				expiration: instance.refreshTokenParsed ? new Date(instance.refreshTokenParsed.exp! * 1000) : new Date(0),
			})
		} catch (error) {
			logError(error)
			setState({ status: 'unauthenticated', reason: 'context' })
		}
	}

	instance.onAuthSuccess = handleUpdate
	instance.onAuthError = handleUpdate
	instance.onAuthRefreshSuccess = handleUpdate
	instance.onAuthRefreshError = handleUpdate
	instance.onAuthLogout = handleUpdate
	instance.onTokenExpired = handleUpdate

	let initPromise: Promise<void> | undefined

	const init = async () => {
		if (state.status !== 'uninitialized' && state.status !== 'initializing') return

		setState({ status: 'initializing', context: state.context })

		if (!initPromise) {
			initPromise = instance
				.init({
					onLoad: 'check-sso',
					silentCheckSsoRedirectUri: `${window.location.origin}/keycloak-silent-check.html`,
				})
				.then((authenticated) => {
					if (!authenticated) {
						return instance.login()
					}
				})
				.catch((error) => {
					setState({ status: 'unauthenticated', reason: 'error' })
					throw error
				})
				.finally(() => {
					initPromise = undefined
				})
		}

		await initPromise
	}

	return {
		subscribe,
		getState: () => state,
		initialize: () => {
			init()
		},
		getToken: async () => {
			try {
				// Wait for keycloak to check authentication. If not logged in, user
				// is redirected to login page.
				await init()
				// Refresh token if it's close to expiration
				await instance.updateToken(updateSkew / 1000)

				return instance.token ?? null
			} catch (error) {
				if (error instanceof Error) {
					throw error
				}
				throw new DefaultError(`Unknown keycloak error of type '${typeof error}'`, { error })
			}
		},
		hasRole: (role) => {
			if (state.status !== 'authenticated') return false

			return state.user.roles.includes(role)
		},
		login: () => {
			instance.login()
		},
		logout: () => {
			instance.logout()
		},
		setContext: (update) => {
			if (state.status !== 'authenticated') return
			setState({ ...state, context: { ...state.context, ...update } })
		},
	}
}
